import { getExtensionValue, MedplumClient } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import zip from "lodash/zip";

import { NEWBORN_POSTPARTUM_PHYSICAL_EXAMS } from "@/encounters/constants/newborn-postpartum";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Extensions } from "@/fhir/enums/extensions";
import { NewbornPostpartumPhysicalExamsQuestionnaires } from "@/fhir/enums/questionnaires";

// Simple in-memory cache for processed diff data
const diffCache = new Map<
  string,
  { data: NewbornPostpartumPhysicalExamDiffResult; timestamp: number }
>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Generate cache key based on parameters
function generateCacheKey(
  encounterId: string,
  versionId?: string,
  currentVersionIndex?: number,
): string {
  return `newborn-postpartum-diff:${encounterId}:${versionId || "null"}:${currentVersionIndex ?? "null"}`;
}

// Check if cache entry is still valid
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_TTL;
}

// Clear cache for a specific encounter (useful when data changes)
export function clearNewbornPostpartumDiffCache(encounterId: string): void {
  const keysToDelete = Array.from(diffCache.keys()).filter((key) => key.includes(encounterId));
  keysToDelete.forEach((key) => diffCache.delete(key));
}

type PhysicalExamKey = keyof typeof NewbornPostpartumPhysicalExamsQuestionnaires;

// Processed format similar to regular physical exams
export interface NewbornPostpartumPhysicalExamSectionAnswer {
  type: "newborn-section";
  sectionName: string;
  normal: boolean;
  notNormal: boolean;
  declined: boolean;
  normalDetails: string[];
  notNormalDetails: string[];
  notes: string;
}

export interface NewbornPostpartumPhysicalExamNotesAnswer {
  type: "newborn-notes";
  notes: string;
}

export type NewbornPostpartumPhysicalExamAnswer =
  | NewbornPostpartumPhysicalExamSectionAnswer
  | NewbornPostpartumPhysicalExamNotesAnswer
  | undefined;

export type NewbornPostpartumPhysicalExamDiffResult = {
  [title: string]: NewbornPostpartumPhysicalExamAnswer[][];
};

// Helper function to parse newborn postpartum physical exam data
function parseNewbornPostpartumPhysicalExamAnswers(
  questionnaireResponse?: QuestionnaireResponse,
): NewbornPostpartumPhysicalExamAnswer[] {
  if (!questionnaireResponse?.item) return [];

  const answers: NewbornPostpartumPhysicalExamAnswer[] = [];
  const sections: Record<string, NewbornPostpartumPhysicalExamSectionAnswer> = {};

  // Process each section in the questionnaire response
  for (const item of questionnaireResponse.item) {
    if (item.linkId === "notes") {
      // Handle general notes
      const notesValue = item.answer?.[0]?.valueString || "";
      if (notesValue) {
        answers.push({
          type: "newborn-notes",
          notes: notesValue,
        });
      }
      continue;
    }

    const sectionLinkId = item.linkId;
    const find = (linkId: string) => item.item?.find((subItem) => subItem.linkId === linkId);

    sections[sectionLinkId] = {
      type: "newborn-section",
      sectionName: sectionLinkId,
      normal: find(`${sectionLinkId}Normal`)?.answer?.[0]?.valueBoolean ?? false,
      notNormal: find(`${sectionLinkId}NotNormal`)?.answer?.[0]?.valueBoolean ?? false,
      declined: find(`${sectionLinkId}Declined`)?.answer?.[0]?.valueBoolean ?? false,
      normalDetails:
        find(`${sectionLinkId}NormalDetails`)
          ?.answer?.map((a) => a.valueCoding?.display || a.valueCoding?.code)
          .filter((item): item is string => Boolean(item)) || [],
      notNormalDetails:
        find(`${sectionLinkId}NotNormalDetails`)
          ?.answer?.map((a) => a.valueCoding?.display || a.valueCoding?.code)
          .filter((item): item is string => Boolean(item)) || [],
      notes: find(`${sectionLinkId}Notes`)?.answer?.[0]?.valueString || "",
    };
  }

  // Add all sections to answers
  answers.push(...Object.values(sections));

  return answers;
}

export async function getNewbornPostpartumPhysicalExamDiffHistory(
  medplum: MedplumClient,
  encounter: Encounter,
  versionId?: string,
  currentVersionIndex?: number,
): Promise<NewbornPostpartumPhysicalExamDiffResult> {
  // Check cache first
  const cacheKey = generateCacheKey(encounter.id!, versionId, currentVersionIndex);
  const cachedEntry = diffCache.get(cacheKey);

  if (cachedEntry && isCacheValid(cachedEntry.timestamp)) {
    return cachedEntry.data;
  }

  const result: NewbornPostpartumPhysicalExamDiffResult = {};

  // Fetch all newborn postpartum physical exam histories in parallel
  await Promise.all(
    NEWBORN_POSTPARTUM_PHYSICAL_EXAMS.map(async (key) => {
      const url = NewbornPostpartumPhysicalExamsQuestionnaires[key as PhysicalExamKey];
      const [history, title] = await fetchAmendmentHistoryByQuestionnaireUrl(
        medplum,
        url,
        encounter.id!,
      );

      if (!history || !title) return;

      // Apply version filtering similar to regular physical exams
      let currentQuestionnaireResponse: QuestionnaireResponse | undefined;
      let previousQuestionnaireResponse: QuestionnaireResponse | undefined;

      if (versionId) {
        // Find the questionnaire response that matches the versionId via extension
        const currentIndex = history.findIndex((qr) => {
          const parentVersionId = getExtensionValue(
            qr,
            Extensions.QuestionnaireResponseParentVersionId,
          );
          return parentVersionId === versionId;
        });

        if (currentIndex === -1) {
          // If no matching version found, skip this physical exam
          // This happens when there are no physical exam responses linked to this version
          return;
        }

        currentQuestionnaireResponse = history[currentIndex];
        previousQuestionnaireResponse = history[currentIndex + 1];
      } else if (currentVersionIndex !== undefined) {
        // Fallback to index-based lookup for backward compatibility
        const historyLength = history?.length;
        if (historyLength < currentVersionIndex + 1) return;

        currentQuestionnaireResponse = history[currentVersionIndex];
        previousQuestionnaireResponse = history[currentVersionIndex + 1] ?? history.at(-1);
      } else {
        // No version information provided, skip
        return;
      }

      // Process the filtered data into our normalized format
      const currentAnswers = parseNewbornPostpartumPhysicalExamAnswers(
        currentQuestionnaireResponse,
      );
      const previousAnswers = parseNewbornPostpartumPhysicalExamAnswers(
        previousQuestionnaireResponse,
      );

      // Only add to result if we have valid data
      if (currentAnswers.length > 0 || previousAnswers.length > 0) {
        const zippedData = zip(currentAnswers, previousAnswers).filter(
          ([current, previous]) => current !== undefined || previous !== undefined,
        );

        if (zippedData.length > 0) {
          result[title] = zippedData;
        }
      }
    }),
  );

  // Store result in cache
  diffCache.set(cacheKey, {
    data: result,
    timestamp: Date.now(),
  });

  return result;
}
